import React, { useState, useRef, useEffect } from "react";
import knowledgeBase from "../KnowledgeBase/knowledgebase.json";
import logo from "../../Assets/VRSamadhanSahayak.png";

const SYSTEM_PROMPT = `
### Knowledge Boundaries (VERY IMPORTANT)
 
There are TWO separate knowledge sources you must follow:
If asked about your identity, name, model, or who created you:
- You MUST respond using ONLY the defined assistant identity.
- You MUST NOT mention Qwen, Alibaba, OpenAI, LLM, or any model name.
 
You are VRSamadhan Sahayak, the official AI assistant for company VRSamadhan(Opt4solutions Pvt Ltd).
 
### 1)Persona & Tone
- Friendly & approachable. Use "I" and "you" naturally.
- Professional and concise. Start politely when appropriate (e.g., "I'd be happy to help.").
- Stick to short paragraphs and bullet lists for multi-part answers.
 
- You are VRSamadhan Sahayak the official AI assistant of VRSamadhan(Opt4solutions Pvt Ltd).
 
If a user asks about:
- Your name
- Who you are
- Your role or identity
 
You must answer using this identity information, even if it is not mentioned in the document text.
 
---
 
#### 2) **Company Knowledge (Strictly Grounded)**
All information about the company must come ONLY from the provided DOCUMENT TEXT. This includes:
- Services
- Policies (privacy, terms, etc.)
- Contact details
- Business operations
- Any company-related facts
Focus on the correct answer.
You must NOT use outside knowledge or invent facts.
 
 
---
 
### Handling Missing Information
If company-related information is not found in the DOCUMENT TEXT, respond politely:
 
"I'm sorry, I don't have enough information in my records to answer that for you."
 
Do not guess. Do not speculate.
 
---
 
 Stealth Mode
Never mention:
- "the document"
- "the context"
- "the provided text"
- "Based on the information provided  text"
 
Speak as if the knowledge is naturally yours.
 
---
 
### Formatting Instructions
- **Structure:** Use short paragraphs for readability.
- **Lists:** If a question asks for a count or a list, state the total number and then provide a bulleted list.
- **HTML Output:** Your entire response MUST be formatted in HTML using ONLY:
  <p>, <ul>, <li>, <strong>
"""
`;

export default function Chatbot() {
	const [messages, setMessages] = useState([
		{
			role: "bot",
			content:
				"<p>Hello! I'm <strong>VRSamadhan Sahayak</strong>. How can I help you today?</p>",
		},
	]);
	const [input, setInput] = useState("");
	const [loading, setLoading] = useState(false);
	const chatRef = useRef(null);

	// 🔽 Auto scroll
	useEffect(() => {
		chatRef.current?.scrollTo(0, chatRef.current.scrollHeight);
	}, [messages]);

	// 🔥 Streaming AI Call
	const callAIStream = async (userInput, onChunk) => {
		try {
			const prompt = `
${SYSTEM_PROMPT}

### KNOWLEDGE BASE
${JSON.stringify(knowledgeBase)}

### Conversation
User: ${userInput}

Assistant:
`;

			const res = await fetch(
				"https://sahayak.opt4solution.site/api/generate",
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
					},
					body: JSON.stringify({
						model: "llama3.1:latest",
						prompt,
						stream: true,
						options: {
							temperature: 0,
						},
					}),
				},
			);

			const reader = res.body.getReader();
			const decoder = new TextDecoder();

			let fullText = "";

			while (true) {
				const { done, value } = await reader.read();
				if (done) break;

				const chunk = decoder.decode(value);
				const lines = chunk.split("\n").filter(Boolean);

				for (const line of lines) {
					const json = JSON.parse(line);
					const token = json.response || "";

					fullText += token;

					onChunk(fullText);

					// optional smooth typing
					await new Promise((r) => setTimeout(r, 25));
				}
			}

			return fullText;
		} catch (err) {
			return "<p>Connection error. Please check if Ollama is running.</p>";
		}
	};

	// 🔥 Send Message
	const sendMessage = async () => {
		if (!input.trim() || loading) return;

		const userMessage = { role: "user", content: input };
		setMessages((prev) => [...prev, userMessage]);
		setInput("");
		setLoading(true);

		let botMessage = { role: "bot", content: "" };
		setMessages((prev) => [...prev, botMessage]);

		await callAIStream(input, (updatedText) => {
			setMessages((prev) => {
				const newMessages = [...prev];
				newMessages[newMessages.length - 1].content = updatedText;
				return newMessages;
			});
		});

		setLoading(false);
	};
	const formatMessage = (html) => {
		if (!html) return "";
		html = html.replace(/">([^<]+)</g, '">$1<');
		if (html.includes("<a")) return html;
		return (
			html
				// Email
				.replace(
					/([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+)/g,
					'<a href="mailto:$1" class="text-blue-600 underline">$1</a>',
				)

				// Phone
				.replace(
					/(\+91[-\s]?\d{10})/g,
					'<a href="tel:$1" class="text-blue-600 underline">$1</a>',
				)

				// Website
				.replace(
					/(https?:\/\/[^\s]+)/g,
					'<a href="$1" target="_blank" class="text-blue-600 underline">$1</a>',
				)

				// www links
				.replace(
					/(www\.[^\s]+)/g,
					'<a href="https://$1" target="_blank" class="text-blue-600 underline">$1</a>',
				)
		);
	};

	return (
		<div
			className="h-screen flex items-center justify-center"
			style={{
				background: "linear-gradient(135deg, #086FB2 0%, #00ACED 100%)",
			}}
		>
			<div className="w-full h-full flex">
				<div className="w-full h-full bg-white flex flex-col overflow-hidden">
					<div className="p-4 border-b flex items-center gap-3 bg-white/70">
						<img src={logo} alt="logo" className="w-10 h-10" />
						<div>
							<p className="font-semibold text-lg">VRSamadhan Sahayak</p>
							<p className="text-sm text-gray-500">AI Assistant</p>
						</div>
					</div>

					<div
						ref={chatRef}
						className="flex-1 p-4 overflow-y-auto space-y-3 bg-blue-50"
					>
						{messages.map((msg, index) => (
							<div
								key={index}
								className={`flex items-end gap-2 ${
									msg.role === "user" ? "justify-end" : "justify-start"
								}`}
							>
								{msg.role === "bot" && (
									<img src={logo} alt="bot" className="w-9 h-9 rounded-full" />
								)}

								{/* Message Bubble */}
								<div
									className={`px-4 py-3 max-w-[75%] text-sm shadow-md ${
										msg.role === "user"
											? "text-white rounded-2xl rounded-br-md"
											: "bg-white text-gray-800 rounded-2xl rounded-bl-md"
									}`}
									style={
										msg.role === "user"
											? {
													background:
														"linear-gradient(135deg, #086FB2 0%, #00ACED 100%)",
												}
											: {}
									}
								>
									{msg.role === "bot" ? (
										<>
											<div
												dangerouslySetInnerHTML={{
													__html: formatMessage(msg.content),
													__html: formatMessage(msg.content),
												}}
											/>

											{loading && index === messages.length - 1 && (
												<span className="typing-dots ml-1">
													<span>.</span>
													<span>.</span>
													<span>.</span>
												</span>
											)}
										</>
									) : (
										msg.content
									)}
								</div>
							</div>
						))}
					</div>

					{/* Input */}
					<div className="p-3 border-t bg-white flex gap-2">
						<input
							value={input}
							onChange={(e) => setInput(e.target.value)}
							placeholder="Type your message..."
							className="flex-1 border rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
							onKeyDown={(e) => e.key === "Enter" && sendMessage()}
						/>
						<button
							onClick={sendMessage}
							className="px-5 rounded-xl text-white font-medium shadow-md"
							style={{
								background: "linear-gradient(135deg, #086FB2 0%, #00ACED 100%)",
							}}
						>
							Send
						</button>
					</div>
				</div>
			</div>
		</div>
	);
}
